/**
 * Machship WooCommerce Blocks Extension
 * Adds suburb/postcode suggestion and address type toggle to the block-based checkout.
 * This file is for WooCommerce Checkout Blocks only.
 * Built with Webpack and Babel for JSX support.
 */

 // Fix: Import React hooks from the global wp.element object
const { useState, useEffect } = window.wp.element || {};
const { __ } = window.wp.i18n || {};

// Safe imports with fallbacks
let registerPlugin, createHigherOrderComponent;

// Check and import registerPlugin
if (window.wp && window.wp.plugins && window.wp.plugins.registerPlugin) {
    registerPlugin = window.wp.plugins.registerPlugin;
} else {
    console.warn('Machship: wp.plugins.registerPlugin is not available');
}

// Check and import createHigherOrderComponent
if (window.wp && window.wp.compose && window.wp.compose.createHigherOrderComponent) {
    createHigherOrderComponent = window.wp.compose.createHigherOrderComponent;
} else {
    console.warn('Machship: wp.compose.createHigherOrderComponent is not available');
    // Fallback createHigherOrderComponent
    createHigherOrderComponent = (mapComponent, displayName) => {
        return (OriginalComponent) => {
            const WrappedComponent = mapComponent(OriginalComponent);
            WrappedComponent.displayName = displayName;
            return WrappedComponent;
        };
    };
}

// Check if required APIs are available
if (!window.wp || !window.wp.element) {
    console.error('Machship: WordPress element (React) is not available');
}

if (!window.wp.plugins || !window.wp.plugins.registerPlugin) {
    console.error('Machship: WordPress plugins API is not available');
}

if (!window.wc || !window.wc.blocksCheckout) {
    console.error('Machship: WooCommerce Blocks checkout API is not available');
}

// Check if hooks are available
if (!useState || !useEffect) {
    console.error('Machship: React hooks (useState, useEffect) are not available');
}

// Settings should be localized from PHP
const MACHSHIP_SETTINGS = window.machshipBlockSettings || {
    enableAddressType: true,
    ajaxurl: '',
    nonce: '',
    is_lock_field: 'yes',
    is_residential: 0,
};

const DISABLE_CHECKOUT_SUBURB_SEARCH =
    MACHSHIP_SETTINGS.disable_checkout_suburb_search === 'yes' ? 'yes' : 'no';
const IS_LOCK_FIELD =
    MACHSHIP_SETTINGS.is_lock_field === 'yes' ? 'yes' : 'no';

const debounce = (func, delay) => {
    let inDebounce;
    return function(...args) {
        clearTimeout(inDebounce);
        inDebounce = setTimeout(() => func.apply(this, args), delay);
    };
};

// Helper to trigger WooCommerce Blocks shipping rates refresh
const refreshBlockShippingRates = () => {
    // Multiple approaches to refresh shipping rates
    
    // Method 1: Dispatch custom event
    const event = new CustomEvent('wc-blocks-checkout-update-shipping-rates');
    document.dispatchEvent(event);
    
    // Method 2: Try to access the store directly via wp.data
    if (window.wp && window.wp.data && window.wp.data.dispatch) {
        try {
            const { dispatch } = window.wp.data;
            // Try to invalidate the cart data
            if (dispatch('wc/store/cart')) {
                dispatch('wc/store/cart').invalidateResolutionForStoreSelector('getCartData');
            }
            // Also try to refresh checkout data
            if (dispatch('wc/store/checkout')) {
                dispatch('wc/store/checkout').invalidateResolutionForStoreSelector('getCheckoutData');
            }
        } catch (e) {
            console.log('Machship: Could not refresh shipping rates via store dispatch');
        }
    }
    
    // Method 3: Try WooCommerce Blocks specific methods
    // if (window.wc && window.wc.blocksCheckout) {
    //     try {
    //         if (window.wc.blocksCheckout.extensionCartUpdate) {
    //             window.wc.blocksCheckout.extensionCartUpdate({
    //                 namespace: 'machship',
    //                 data: { refresh: true }
    //             });
    //         }
    //     } catch (e) {
    //         // Suppress error to avoid console noise
    //     }
    // }
};

/**
 * Use WooCommerce Blocks data API to read shipping and billing postcode fields.
 * No custom postcode input is rendered; values are read from the checkout store.
 */
const { useSelect } = window.wp.data || {};

const { useDispatch } = window.wp.data || {};

function machshipBlockFindShippingCosts({ city, state, postcode, is_residential, nonce, ajaxurl, country }) {
    // Compose data similar to classic checkout

   let data;

    if (DISABLE_CHECKOUT_SUBURB_SEARCH === 'yes') {
        // for disable suburb search suggest and international shipping
        data = {
            'action': 'woo_machship_set_fields_on_session',
            'tosuburb': city,
            'tostate': state,
            'topostcode': postcode,
            'is_residential': is_residential,
            'tocountry': country,
            'nonce': nonce
        };
    } else {
        data = {
            action: 'woo_machship_set_fields_on_session',
            tosuburb: city,
            tostate: state,
            topostcode: postcode,
            is_residential: is_residential,
            nonce: nonce,
            tocountry: country
        };
    }

    // Include address lines if present in DOM (Blocks IDs use hyphen-underscore pattern)
    const addr1 =
        (document.querySelector('#shipping-address_1')?.value) ||
        (document.querySelector('#billing-address_1')?.value) || '';
    const addr2 =
        (document.querySelector('#shipping-address_2')?.value) ||
        (document.querySelector('#billing-address_2')?.value) || '';
    if (addr1) { data.address_line_1 = addr1; }
    if (addr2) { data.address_line_2 = addr2; }

    // Send AJAX request to update session and trigger backend logic
    fetch(ajaxurl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'X-WP-Nonce': nonce || '',
        },
        body: new URLSearchParams(data),
    })
    .then(() => {
        refreshBlockShippingRates();
    })
    .catch(e => {
        console.error('[Machship] Error recalculating block shipping costs:', e);
    });
}

/**
 * Helper: persist chosen suburb to PHP session (parity with legacy checkout)
 * @param {{ suburb:string, state:string, postcode:string, is_residential:boolean }} param0
 * @returns {Promise<Response>}
 */
function setShippingSuburbSession({ suburb, state, postcode, is_residential, locationId }) {
    const payload = {
        action: 'woo_machship_set_shipping_suburb',
        tosuburb: suburb,
        tostate: state,
        topostcode: postcode,
        nonce: MACHSHIP_SETTINGS.nonce,
        is_residential,
    };
    if (locationId !== undefined) {
        payload.locationId = locationId;
    }
    return fetch(MACHSHIP_SETTINGS.ajaxurl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'X-WP-Nonce': MACHSHIP_SETTINGS.nonce || '',
        },
        body: new URLSearchParams(payload),
    }).catch((e) =>
        console.error('[Machship] Error setting shipping suburb (blocks):', e),
    );
}

const ShippingPostcodeSuggestionDropdown = () => {
    if (DISABLE_CHECKOUT_SUBURB_SEARCH === 'yes') {
        return null;
    }
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [postcode, setPostcode] = useState('');
    const [hasTyped, setHasTyped] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(() => {
        const input = document.querySelector('#shipping-country');
        return input ? input.value : 'AU';
    });
    const extensionDispatch = useDispatch('wc/store/checkout') || {};
    const { setExtensionData } = extensionDispatch;

     // honour classic setting: skip postcode picker entirely
  

    // Lock/unlock city/state/postcode based on country and suburb search setting
    useEffect(() => {
        const cityInput = document.querySelector('#shipping-city');
        const stateInput = document.querySelector('#shipping-state');
        if (selectedCountry === 'AU' && DISABLE_CHECKOUT_SUBURB_SEARCH === 'no' && IS_LOCK_FIELD === 'yes') {
            cityInput?.setAttribute('readonly', 'true');
            stateInput?.setAttribute('disabled', 'true');
        } else {
            cityInput?.removeAttribute('readonly');
            stateInput?.removeAttribute('disabled');
        }
    }, [selectedCountry]);

    // Listen to DOM input changes
    useEffect(() => {
        const input = document.querySelector('#shipping-postcode');
        if (!input) return;

        const handleInput = (e) => {
            setPostcode(e.target.value);
            setHasTyped(true);
        };
        input.addEventListener('input', handleInput);

        // Set initial value (do not show dropdown on load)
        setPostcode(input.value);

        return () => {
            input.removeEventListener('input', handleInput);
        };
    }, []);

    // Listen to country input changes and keep state in sync
    useEffect(() => {
        const input = document.querySelector('#shipping-country');
        if (!input) return;
        const handleCountryChange = (e) => setSelectedCountry(e.target.value);
        input.addEventListener('change', handleCountryChange);
        // Set initial value
        setSelectedCountry(input.value);
        return () => {
            input.removeEventListener('change', handleCountryChange);
        };
    }, []);

    // Debounced fetch for suburb suggestions
    useEffect(() => {
        if (selectedCountry !== 'AU') {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }
        if (!hasTyped || !postcode || postcode.length < 4) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }
        setLoading(true);
        fetch(MACHSHIP_SETTINGS.ajaxurl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                'X-WP-Nonce': MACHSHIP_SETTINGS.nonce || '',
            },
            body: new URLSearchParams({
                action: 'get_location',
                keyword: postcode,
                nonce: MACHSHIP_SETTINGS.nonce,
                block_checkout: 1,
            }),
        })
            .then(res => res.json())
            .then(data => {
                if (data.type === 'success' && Array.isArray(data.result)) {
                    setSuggestions(data.result);
                    setShowDropdown(true);
                } else {
                    setSuggestions([]);
                    setShowDropdown(false);
                }
            })
            .catch(() => {
                setSuggestions([]);
                setShowDropdown(false);
            })
            .finally(() => setLoading(false));
    }, [postcode, hasTyped, selectedCountry]);

    // Handle suggestion select: update shipping address in checkout store
    const [shippingAddress, setShippingAddress] = useState({
        city: '',
        state: '',
        postcode: ''
    });

    const handleSuggestionSelect = (s) => {
        const suburb = typeof s === "string" ? s : s.suburb || s.label || '';
        const state = typeof s === "object" && (s.state || s.state_code || s.stateName) ? (s.state || s.state_code || s.stateName) : '';

        // Update the shipping address state
        setShippingAddress({
            city: suburb,
            state: state,
            postcode: postcode,
            country: selectedCountry
        });

        setShowDropdown(false);

        // Persist suburb to session via helper
        // Extract locationId from suggestion if present
        const locationId = typeof s === "object" && s.locationId !== undefined ? s.locationId : undefined;

        setShippingSuburbSession({
            suburb,
            state,
            postcode,
            is_residential:
                document.querySelector('input[name="machship-address-type"]:checked')
                    ?.value === 'residential',
            locationId
        }).then(() => {
            // Also update via classic AJAX for session/backend logic
            machshipBlockFindShippingCosts({
                city: suburb,
                state: state,
                postcode: postcode,
                is_residential: document.querySelector('input[name="machship-address-type"]:checked')?.value === 'residential' ? 1 : 0,
                nonce: MACHSHIP_SETTINGS.nonce,
                ajaxurl: MACHSHIP_SETTINGS.ajaxurl,
                country: selectedCountry
            });

            if (setExtensionData) {
                try {
                    setExtensionData('machship', {
                        city: suburb,
                        state: state,
                        postcode: postcode,
                    });
                } catch (e) {
                    console.error('[Machship] Error calling setExtensionData:', e);
                }
            }
        });

        // Update WooCommerce Blocks checkout fields via data flow API
        if (window.wp && window.wp.data && window.wp.data.dispatch) {
            wp.data.dispatch('wc/store/checkout').setAdditionalFields({
                shipping_address: {
                    city: suburb,
                    state: state,
                    postcode: postcode,
                    country: selectedCountry,
                },
            });
        }

        // Also update the DOM fields for visual feedback and trigger events
        const countryInput = document.querySelector('#shipping-country');
        if (countryInput && countryInput.value !== selectedCountry) {
            countryInput.value = selectedCountry;
            countryInput.dispatchEvent(new Event('input', { bubbles: true }));
            countryInput.dispatchEvent(new Event('change', { bubbles: true }));
            countryInput.blur();
            countryInput.focus();
        }

        const cityInput = document.querySelector('#shipping-city');
        if (cityInput) {
            // Simulate user typing the value character by character
            cityInput.value = '';
            for (let i = 0; i < suburb.length; i++) {
                cityInput.value += suburb[i];
                cityInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
            cityInput.dispatchEvent(new Event('change', { bubbles: true }));
            cityInput.blur();
            cityInput.focus();
        } else {
            console.warn('[Machship] #shipping-city not found in DOM');
        }
        const stateInput = document.querySelector('#shipping-state');
        if (stateInput) {
            stateInput.value = state;
            stateInput.dispatchEvent(new Event('input', { bubbles: true }));
            stateInput.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
            console.warn('[Machship] #shipping-state not found in DOM');
        }
    };

    return (
        <div className="machship-postcode-suggestion-dropdown" style={{ position: "relative" }}>
            <div style={{ fontSize: "13px", marginBottom: "6px", color: "#333" }}>
                Enter your postcode and/or suburb to display a list of suburbs.
            </div>
            {loading && <div style={{ fontSize: "12px" }}>Loading suggestions...</div>}
            {showDropdown && suggestions.length > 0 && (
                <ul style={{
                    position: "absolute",
                    zIndex: 10,
                    background: "#fff",
                    border: "1px solid #ccc",
                    width: "100%",
                    maxHeight: "120px",
                    overflowY: "auto",
                    margin: 0,
                    padding: "4px 0",
                    listStyle: "none"
                }}>
                    {suggestions.map((s, i) => (
                        <li
                            key={i}
                            style={{ padding: "4px 12px", cursor: "pointer" }}
                            onClick={() => {
                                handleSuggestionSelect(s);
                            }}
                        >
                            {typeof s === "object"
                                ? `${s.suburb || ''}${s.state ? ', ' + s.state : ''}`
                                : s}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const AddressTypeToggle = () => {
    const initialType = (MACHSHIP_SETTINGS.is_residential === 1 || MACHSHIP_SETTINGS.is_residential === '1') ? 'residential' : 'business';
    const [type, setType] = useState(initialType);

    if (MACHSHIP_SETTINGS.enableAddressType !== "yes") return null;

    // On mount, ensure the correct address type is sent to the backend/session
    useEffect(() => {
        const data = {
            action: 'woo_machship_set_fields_on_session',
            is_residential: type === 'residential' ? 1 : 0,
            nonce: MACHSHIP_SETTINGS.nonce,
        };
        fetch(MACHSHIP_SETTINGS.ajaxurl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                'X-WP-Nonce': MACHSHIP_SETTINGS.nonce || '',
            },
            body: new URLSearchParams(data),
        }).then(() => {
            refreshBlockShippingRates();
        }).catch((e) => {
            console.error('Machship: Error updating address type on load', e);
        });
    }, []); // Run only on mount

    const handleTypeChange = async (e) => {
        const newType = e.target.value;
        setType(newType);

        // Update session with address type and refresh shipping rates
        const data = {
            action: 'woo_machship_set_fields_on_session',
            is_residential: newType === 'residential' ? 1 : 0,
            nonce: MACHSHIP_SETTINGS.nonce,
        };
        try {
            await fetch(MACHSHIP_SETTINGS.ajaxurl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                    'X-WP-Nonce': MACHSHIP_SETTINGS.nonce || '',
                },
                body: new URLSearchParams(data),
            });
            refreshBlockShippingRates();
        } catch (e) {
            console.error('Machship: Error updating address type', e);
        }
    };

    // Custom radio-to-checkbox style
    const radioStyle = {
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        width: '22px',
        height: '22px',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: '#007cba',
        borderRadius: '4px',
        backgroundColor: '#fff',
        marginRight: '8px',
        position: 'relative',
        verticalAlign: 'middle',
        marginTop: '5px', // Fine-tune this value for perfect alignment
        outline: 'none',
        cursor: 'pointer',
        transition: 'border-color 0.2s'
    };
    // Checked style now shows a ✓ tick instead of solid fill
    const radioCheckedStyle = {
        ...radioStyle,
        boxShadow: '0 0 0 2px #cce6ff',
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23007cba'><path d='M6.173 13.923L.668 8.418l2.449-2.449 3.056 3.056 7.21-7.21 2.448 2.449z'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '70% 70%',
    };

    return (
        <div className="machship-address-type" style={{ fontSize: '1.25em', margin: '18px 0' }}>
            <label style={{ fontWeight: 'bold', fontSize: '1.2em', marginBottom: '8px', display: 'block' }}>
                Address Type:
            </label>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'center' }}>
                <label style={{ display: 'flex', fontSize: '1em', cursor: 'pointer' }}>
                    <input
                        type="radio"
                        name="machship-address-type"
                        value="residential"
                        checked={type === 'residential'}
                        onChange={handleTypeChange}
                        style={type === 'residential' ? radioCheckedStyle : radioStyle}
                    />
                    <span style={{ userSelect: 'none' }}>Residential</span>
                </label>
                <label style={{ display: 'flex', fontSize: '1em', cursor: 'pointer' }}>
                    <input
                        type="radio"
                        name="machship-address-type"
                        value="business"
                        checked={type === 'business'}
                        onChange={handleTypeChange}
                        style={type === 'business' ? radioCheckedStyle : radioStyle}
                    />
                    <span style={{ userSelect: 'none' }}>Business</span>
                </label>
            </div>
        </div>
    );
};


const { createElement, createRoot } = window.wp.element || {};
const { render: legacyRender } = window.wp.element || {};

//Define your component at the top level
const MachshipShippingExtension = () => {
    return (
        <div className="machship-shipping-extension">
            {MACHSHIP_SETTINGS.enableAddressType === "yes" && <AddressTypeToggle />}
        </div>
    );
};


function mountShippingPostcodeDropdown() {
    const input = document.querySelector('#shipping-postcode');
    if (!input) return;

    // Avoid duplicate containers
    if (input.nextSibling && input.nextSibling.classList && input.nextSibling.classList.contains('machship-postcode-suggestion-dropdown')) {
        return;
    }

    // Create container after the postcode input
    const container = document.createElement('div');
    container.className = 'machship-postcode-suggestion-dropdown';
    container.style.position = 'relative';
    input.parentNode.insertBefore(container, input.nextSibling);

    // Render the React dropdown into the container
    if (createRoot) {
        const root = createRoot(container);
        root.render(createElement(ShippingPostcodeSuggestionDropdown));
    } else if (legacyRender) {
        legacyRender(createElement(ShippingPostcodeSuggestionDropdown), container);
    }
}

/**
 * Observe for address fields (city, state, postcode, country) in the DOM and attach API logic robustly.
 */
function attachMachshipAddressListeners() {

    const cityInput = document.querySelector('#shipping-city');
    const stateInput = document.querySelector('#shipping-state');
    const postcodeInput = document.querySelector('#shipping-postcode');
    const countryInput = document.querySelector('#shipping-country');

    if (!cityInput || !stateInput || !postcodeInput || !countryInput) return;

    // Always remove previous listeners to avoid duplicates or stale handlers
    cityInput.removeEventListener('change', window.__machshipAddressChangeHandler);
    stateInput.removeEventListener('change', window.__machshipAddressChangeHandler);
    postcodeInput.removeEventListener('change', window.__machshipAddressChangeHandler);
    countryInput.removeEventListener('change', window.__machshipAddressChangeHandler);

    // Only attach handler if suburb search is disabled or not AU
    if (DISABLE_CHECKOUT_SUBURB_SEARCH === 'no' && countryInput.value === 'AU') {
        // Do not attach handler
        return;
    }

    // Debounced handler to avoid race with WooCommerce Blocks re-render
    window.__machshipAddressChangeHandler = debounce(function() {

        const city = cityInput.value || '';
        const state = stateInput.value || '';
        const postcode = postcodeInput.value || '';
        const country = countryInput.value || 'AU';

        // Only trigger if all fields are filled
        if (!city || !state || !postcode || !country) return;

        console.log('Machship API Triggered (debounced observer):', { city, state, postcode, country });

        // Update WooCommerce Blocks store first so value persists
        if (window.wp && window.wp.data && window.wp.data.dispatch) {
            window.wp.data.dispatch('wc/store/checkout').setAdditionalFields({
                shipping_address: {
                    city,
                    state,
                    postcode,
                    country,
                },
            });
        }

        machshipBlockFindShippingCosts({
            city,
            state,
            postcode,
            is_residential: document.querySelector('input[name="machship-address-type"]:checked')?.value === 'residential' ? 1 : 0,
            nonce: MACHSHIP_SETTINGS.nonce,
            ajaxurl: MACHSHIP_SETTINGS.ajaxurl,
            country
        });

    }, 600);

    cityInput.addEventListener('change', window.__machshipAddressChangeHandler);
    stateInput.addEventListener('change', window.__machshipAddressChangeHandler);
    postcodeInput.addEventListener('change', window.__machshipAddressChangeHandler);
    countryInput.addEventListener('change', window.__machshipAddressChangeHandler);
}

// Observe for the postcode field in the DOM
const observer = new MutationObserver(() => {
    mountShippingPostcodeDropdown();
    attachMachshipAddressListeners();
});
observer.observe(document.body, { childList: true, subtree: true });

// Also try to mount immediately in case the field is already present
setTimeout(mountShippingPostcodeDropdown, 500);
setTimeout(attachMachshipAddressListeners, 500);

// Check environment and wait for the checkout block
if (!createElement || (!createRoot && !legacyRender)) {
    console.warn('[Machship] React environment not available');
} else {
    const interval = setInterval(() => {
        const shippingBlock = document.querySelector('.wp-block-woocommerce-checkout-shipping-address-block');

        if (!shippingBlock) {
            return;
        }

        // Prevent duplicate mount if container already exists
        clearInterval(interval);
        if (shippingBlock.querySelector('.machship-shipping-extension')) {
            return;
        }

        const container = document.createElement('div');
        container.className = 'machship-shipping-extension';
        shippingBlock.prepend(container);

        // Support legacy render fallback if createRoot is not available
        if (createRoot) {
            const root = createRoot(container);
            root.render(<MachshipShippingExtension />);
        } else {
            legacyRender(<MachshipShippingExtension />, container);
        }
    }, 500);
}


// Method 2: Higher-order component approach (only if createHigherOrderComponent is available)
let withMachshipExtension = null;

if (createHigherOrderComponent) {
    withMachshipExtension = createHigherOrderComponent((OriginalComponent) => {
        return (props) => {
            return window.wp.element.createElement(
                'div',
                null,
                window.wp.element.createElement(OriginalComponent, props),
                window.wp.element.createElement(MachshipShippingExtension)
            );
        };
    }, 'withMachshipExtension');
}

// Register the plugin (only if registerPlugin is available)
// (Removed legacy MachshipCheckoutPlugin registration - now handled by Blocks extensibility API)

// Try to hook into WooCommerce Blocks filters if available
if (window.wp && window.wp.hooks && window.wp.hooks.addFilter) {
    // Test direct filter for select_after
    window.wp.hooks.addFilter(
        'woocommerce_blocks_checkout_shipping_methods_select_after',
        'machship/shipping-extension-test',
        (OriginalComponent) => {
            return window.wp.element.createElement(
                'div',
                { style: { border: '2px solid red', padding: '10px', margin: '10px 0' } },
                'Machship Test Injection'
            );
        }
    );
    // Try various filter hooks that might be available
    if (withMachshipExtension) {
        const filterHooks = [
            'woocommerce_blocks_checkout_shipping_methods_select_after',
            'woocommerce_blocks_checkout_shipping_methods_select_before',
            'woocommerce_blocks_checkout_shipping_methods',
            'woocommerce_blocks_checkout_shipping_address_component',
            'woocommerce.checkout.shipping-address.render',
            'woocommerce_blocks_checkout_step_shipping_address',
            'woocommerce_blocks_checkout_form'
        ];
        
        filterHooks.forEach(hook => {
            try {
                window.wp.hooks.addFilter(
                    hook,
                    'machship/shipping-extension',
                    withMachshipExtension
                );
            } catch (e) {
                // Silently continue if hook doesn't exist
            }
        });
    }
} else {
    console.warn('Machship: wp.hooks.addFilter or withMachshipExtension not available');
}

// Fallback: Direct DOM manipulation for more robust injection
const initializeMachshipExtension = () => {
    const checkForCheckoutAndInject = () => {
        const checkoutForm = document.querySelector('.wc-block-checkout');
        if (checkoutForm) {
            const shippingSection = checkoutForm.querySelector('.wc-block-checkout__shipping-address');
            if (shippingSection && !document.querySelector('.machship-shipping-extension')) {
                const container = document.createElement('div');
                container.id = 'machship-extension-fallback';
                shippingSection.appendChild(container);
                
                if (window.wp && window.wp.element && window.wp.element.render) {
                    window.wp.element.render(
                        window.wp.element.createElement(MachshipShippingExtension),
                        container
                    );
                }
            }
        }
    };
    
    // Try multiple times with different delays
    setTimeout(checkForCheckoutAndInject, 100);
    setTimeout(checkForCheckoutAndInject, 500);
    setTimeout(checkForCheckoutAndInject, 1000);
    setTimeout(checkForCheckoutAndInject, 2000);
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeMachshipExtension);
} else {
    initializeMachshipExtension();
}

// Also initialize on page load for safety
window.addEventListener('load', initializeMachshipExtension);

/**
 * Direct class override: replace en‑dash HTML entities in Checkout/Cart Blocks labels.
 * Targets:
 *  - .wc-block-components-totals-item__label (Shipping, Subtotal, etc)
 *  - .wc-block-components-shipping-rate__label (rate selector)
 * Handles both encoded and double-encoded entities.
 */
(function msOverrideTotalsLabelDash() {
    try {
        var SELECTORS = '.wc-block-components-totals-item__label, .wc-block-components-shipping-rate__label';
        var RE = /&#8211;|&ndash;|&#x2013;|&#8211;|&ndash;|&#x2013;/g;
        var EN = '\u2013';

        function fix(root) {
            var ctx = (root && root.querySelectorAll) ? root : document;
            var els = ctx.querySelectorAll(SELECTORS);
            if (!els || !els.length) return;
            els.forEach(function (el) {
                try {
                    // Fix text nodes first, then fallback to innerHTML replacement
                    try {
                        var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
                        var n;
                        while ((n = walker.nextNode())) {
                            var v = n.nodeValue || '';
                            var nv = v.replace(RE, EN);
                            if (nv !== v) { n.nodeValue = nv; }
                        }
                    } catch (e) {}
                    var html = el.innerHTML || '';
                    var newHtml = html.replace(RE, EN);
                    if (newHtml !== html) {
                        el.innerHTML = newHtml;
                    }
                } catch (e) {}
            });
        }

        function run() {
            fix(document);
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', run);
        } else {
            run();
        }
        window.addEventListener('load', run);

        // Observe re-renders from Blocks UI
        var container =
            document.querySelector('.wc-block-components-totals') ||
            document.querySelector('.wc-block-checkout') ||
            document.querySelector('.wc-block-cart') ||
            document.body;

        if (container && !window.__msDashTotalsObs) {
            window.__msDashTotalsObs = new MutationObserver(function (muts) {
                muts.forEach(function (m) {
                    if (m.type === 'childList' && m.addedNodes && m.addedNodes.length) {
                        m.addedNodes.forEach(function (n) {
                            if (n && n.nodeType === 1) {
                                // If the node is a label or contains labels, fix it
                                if (n.matches && n.matches(SELECTORS)) {
                                    fix(n);
                                } else {
                                    fix(n);
                                }
                            }
                        });
                    } else if (m.type === 'characterData' && m.target && m.target.parentElement) {
                        var p = m.target.parentElement;
                        if (p.matches && p.matches(SELECTORS)) {
                            fix(p);
                        }
                    }
                });
            });
            window.__msDashTotalsObs.observe(container, {
                subtree: true,
                childList: true,
                characterData: true
            });
        }

        // When our plugin triggers a shipping rates refresh event, re-run after render
        function scheduleFixRuns() {
            try {
                setTimeout(run, 0);
                setTimeout(run, 80);
                setTimeout(run, 200);
                setTimeout(run, 400);
                if (window.requestAnimationFrame) {
                    requestAnimationFrame(run);
                    setTimeout(function(){ requestAnimationFrame(run); }, 120);
                }
            } catch (e) {}
        }
        document.addEventListener('wc-blocks-checkout-update-shipping-rates', function () {
            scheduleFixRuns();
        });
        // Also run after address type changes (radio inputs) to cover re-render timing
        document.addEventListener('change', function(ev) {
            var t = ev.target || ev.srcElement;
            if (t && t.name === 'machship-address-type') {
                scheduleFixRuns();
            }
        });
    } catch (e) {}
})();

// Export for potential use in other scripts
window.MachshipShippingExtension = MachshipShippingExtension;
